import { Eye, DollarSign } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useState } from "react";

const RequestCard = ({
  request,
  t,
  styles,
  language,
  getEmployeeName,
  getStatusText,
  getUrgencyText,
  getStatusIcon,
  formatCurrency,
  onViewDetails,
  onProcess,
  onDisburse,
}) => {
  const { hasFeatureAccess, user } = useAuth();
  // Was declared with no setter and no mouse handlers wired up, so the hover
  // lift transform below could never actually trigger -- dead code.
  const [isHovered, setIsHovered] = useState(false);
  
  const normalizedStatus = String(request?.status || "").toLowerCase();
  const canView = hasFeatureAccess("advancements", "view") || user?.role === "admin" || user?.role === "superadmin";
  const canActOnRequest = Boolean(request?.currentUserCanAct) && normalizedStatus === "pending";
  // Require BOTH: workflow-assigned to act on this request AND the admin-level feature grant.
  const canApprove = canActOnRequest && hasFeatureAccess("advancements", "approve");
  const canReject = canActOnRequest && hasFeatureAccess("advancements", "reject");
  const canProcess = canApprove || canReject;
  // Disburse: request approved + workflow permits this user to disburse + feature grant
  // ('approve' still implies disburse for admin levels granted only 'approve').
  const canDisburse =
    normalizedStatus === "approved" &&
    Boolean(request?.currentUserCanDisburse) &&
    (hasFeatureAccess("advancements", "approve") || hasFeatureAccess("advancements", "disburse"));

  return (
    <div
      style={{
        ...styles.requestCard,
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
        transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
        cursor: "pointer",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.requestHeader}>
        <div style={styles.requestInfo}>
          <h4 style={styles.employeeName}>{getEmployeeName(request)}</h4>

          <p style={styles.requestDetail}>
            <strong>{t.employeeCode}:</strong> {request.clientCode || request.employee?.clientCode || request.infinityCode}
          </p>

          <p style={styles.requestDetail}>
            <strong>{t.amount}:</strong> {formatCurrency(request.requestedAmount)}
          </p>

          <p style={styles.requestDetail}>
            <strong>{t.installments}:</strong>{" "}
            {request.numberOfInstallments || "N/A"}
            {request.installmentAmount && (
              language === "ar"
                ? ` (${request.installmentAmount.toLocaleString("ar-EG")} ج.م لكل قسط)`
                : ` (EGP ${request.installmentAmount.toLocaleString()} each)`
            )}
          </p>

          <p style={styles.requestDetail}>
            <strong>{t.reason}:</strong> {request.reason || "N/A"}
          </p>

          <p style={styles.requestDetail}>
            <strong>{t.urgency}:</strong> {getUrgencyText(request.urgency)}
          </p>

          <p style={styles.requestDetail}>
            <strong>{t.requestedDate}:</strong>{" "}
            {new Date(
              request.requestedDate || request.created_at
            ).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
          </p>

          {request.remainingBalance !== undefined && (
            <p style={styles.requestDetail}>
              <strong>{t.remaining}:</strong>{" "}
              {formatCurrency(request.remainingBalance)}
            </p>
          )}
        </div>

        <div>
          <div style={styles.statusBadge(request.status)}>
            {getStatusIcon(request.status)}
            {getStatusText(request.status)}
          </div>
        </div>
      </div>

      <div style={styles.buttonGroup}>
        {canView && (
          <button style={styles.secondaryButton} onClick={() => onViewDetails(request)}>
            <Eye size={16} /> {t.viewDetails}
          </button>
        )}

        {request.status === "pending" && canProcess && (
          <button style={styles.button} onClick={() => onProcess(request)}>
            {t.processRequest}
          </button>
        )}

        {request.status === "approved" && canDisburse && (
          <button style={styles.button} onClick={() => onDisburse(request)}>
            <DollarSign size={16} /> {t.disburse}
          </button>
        )}
      </div>
    </div>
  );
};

export default RequestCard;